import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import { useCodeMirrorViewContext } from "../../codemirror-view-context.js";
import { useSelectionContext } from "../../contexts/selection-context.js";
import { useTableContext } from "../../contexts/table-context.js";
import { useTabularContext } from "../../contexts/tabular-context.js";
import { setColumnWidth } from "../commands.js";
import { UNITS } from "./column-width.js";

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.28);
    z-index: 2400;
`;

const Dialog = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(460px, calc(100vw - 32px));
    border-radius: 12px;
    border: 1px solid #d7e0ea;
    background: #fff;
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
    z-index: 2401;
`;

const Header = styled.div`
    padding: 16px 18px;
    border-bottom: 1px solid #e7edf4;
    font-size: 16px;
    font-weight: 700;
    color: #23384d;
`;

const Body = styled.div`
    padding: 18px;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 0 18px 18px;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 6px;
    color: #42576b;
    font-size: 13px;
    font-weight: 600;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 120px;
    gap: 10px;
    align-items: end;
`;

const Input = styled.input`
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid #cfd9e5;
    border-radius: 8px;
    color: #23384d;
    font-size: 14px;
`;

const Select = styled.select`
    width: 100%;
    height: 40px;
    padding: 0 10px;
    border: 1px solid #cfd9e5;
    border-radius: 8px;
    color: #23384d;
    font-size: 14px;
    background: #fff;
`;

const Help = styled.p`
    margin: 10px 0 0;
    color: #6b7f92;
    font-size: 12px;
    line-height: 1.5;
`;

const Button = styled.button`
    height: 36px;
    padding: 0 14px;
    border-radius: 8px;
    border: 1px solid ${(props) => (props.$primary ? "#1a8f73" : "#cfd9e5")};
    background: ${(props) => (props.$primary ? "#1a8f73" : "#fff")};
    color: ${(props) => (props.$primary ? "#fff" : "#42576b")};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
`;

export function ColumnWidthModal() {
    const { columnWidthModalShown, closeColumnWidthModal } = useTabularContext();
    const view = useCodeMirrorViewContext();
    const { selection } = useSelectionContext();
    const { positions, table } = useTableContext();
    const inputRef = useRef(null);
    const [currentUnit, setCurrentUnit] = useState("%");
    const [currentWidth, setCurrentWidth] = useState("");

    const unitHelp = useMemo(() => {
        if (currentUnit === "%") {
            return "퍼센트는 현재 줄 너비를 기준으로 한 비율입니다.";
        }
        if (currentUnit === "custom") {
            return "유효한 LaTeX 길이 값이나 명령을 직접 입력하세요.";
        }
        return "";
    }, [currentUnit]);

    useEffect(() => {
        if (!columnWidthModalShown) {
            return;
        }
        inputRef.current?.focus();
        setCurrentUnit("%");
        setCurrentWidth("");
    }, [columnWidthModalShown]);

    if (!columnWidthModalShown) {
        return null;
    }

    const onSubmit = (event) => {
        event.preventDefault();
        if (selection && currentUnit) {
            setColumnWidth(
                view,
                selection,
                {
                    unit: currentUnit,
                    width: currentUnit === "custom" ? currentWidth : parseFloat(currentWidth || "0"),
                },
                positions,
                table,
            );
        }
        closeColumnWidthModal();
    };

    return createPortal(
        <>
            <Backdrop onClick={closeColumnWidthModal} />
            <Dialog className="table-generator-width-modal" role="dialog" aria-modal="true">
                <Header>열 너비 설정</Header>
                <Body>
                    <form id="table-generator-width-form" onSubmit={onSubmit}>
                        <Row>
                            <div>
                                <Label htmlFor="column-width-input">열 너비</Label>
                                <Input
                                    id="column-width-input"
                                    ref={inputRef}
                                    value={currentWidth}
                                    required
                                    onChange={(event) => setCurrentWidth(event.target.value)}
                                    type={currentUnit === "custom" ? "text" : "number"}
                                />
                            </div>
                            <div>
                                <Label htmlFor="column-width-unit">단위</Label>
                                <Select
                                    id="column-width-unit"
                                    value={currentUnit}
                                    onChange={(event) => setCurrentUnit(event.target.value)}
                                >
                                    {UNITS.map((unit) => (
                                        <option key={unit} value={unit}>
                                            {unit}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        </Row>
                        {unitHelp ? <Help>{unitHelp}</Help> : null}
                    </form>
                </Body>
                <Footer>
                    <Button type="button" onClick={closeColumnWidthModal}>
                        취소
                    </Button>
                    <Button type="submit" form="table-generator-width-form" $primary>
                        확인
                    </Button>
                </Footer>
            </Dialog>
        </>,
        document.body,
    );
}
